import type { Restaurant } from "@/types/restaurant";

/**
 * Pure helpers for whether a venue is currently open.
 *
 * Both windows that fit in a single day (11:00→23:00) and ones that wrap
 * past midnight (16:00→02:00) are handled — `closes_at` smaller than
 * `opens_at` is the unambiguous signal of a wrap.
 */

function parseHHMM(s: string): { h: number; m: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return { h, m: min };
}

function minutesSinceMidnight(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

function hhmmToMinutes(s: string): number | null {
  const p = parseHHMM(s);
  return p ? p.h * 60 + p.m : null;
}

/** 24-hour windows: open == close, or 00:00 → 23:59. */
function isAlwaysOpen(open: number, close: number): boolean {
  return open === close || (open === 0 && close >= 23 * 60 + 59);
}

function minutesToHHMM(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

export function isRestaurantOpen(
  restaurant: Pick<Restaurant, "opens_at" | "closes_at">,
  now: Date = new Date(),
): boolean {
  const open = hhmmToMinutes(restaurant.opens_at);
  const close = hhmmToMinutes(restaurant.closes_at);
  if (open === null || close === null) return true; // bad config — fail open
  if (isAlwaysOpen(open, close)) return true;

  const cur = minutesSinceMidnight(now);

  if (close > open) {
    // Same-day window: open ≤ now < close
    return cur >= open && cur < close;
  }
  // Wrap-past-midnight: open until tomorrow's `close`.
  return cur >= open || cur < close;
}

/** Human-readable hours label, e.g. "11:00–23:30". */
export function formatOpeningHours(r: Pick<Restaurant, "opens_at" | "closes_at">): string {
  return `${r.opens_at}–${r.closes_at}`;
}

/**
 * What the card's status chip says next to "Open" / "Closed":
 *   open   → "until" the closing time ("Open · until 23:30");
 *   closed → "opens" at the opening time ("Closed · opens 12:00");
 *   24h windows (00:00–23:59, or open == close) and malformed hours → "plain"
 *   (just "Open"; malformed hours already fail open in `isRestaurantOpen`).
 */
export type OpenStatusDetail =
  | { kind: "until"; time: string }
  | { kind: "opens"; time: string }
  | { kind: "plain" };

export function openStatusDetail(
  r: Pick<Restaurant, "opens_at" | "closes_at">,
  isOpen: boolean,
): OpenStatusDetail {
  const open = hhmmToMinutes(r.opens_at);
  const close = hhmmToMinutes(r.closes_at);
  if (open === null || close === null || isAlwaysOpen(open, close)) return { kind: "plain" };
  return isOpen ? { kind: "until", time: minutesToHHMM(close) } : { kind: "opens", time: minutesToHHMM(open) };
}
